import shp from 'shpjs'
import JSZip from 'jszip'
import type { GeoPointFeature } from '../../../types'

/** Retorna "(xx.xx%)" da razão adicionados/total (ou 0 se total for 0). */
export function calcularPorcentagem(adicionados: number, total: number): string | number {
    if (total === 0) return 0
    const resultado = (adicionados / total) * 100
    return `(${resultado.toFixed(2)}%)`
}

/** Deixa o texto seguro para nome de arquivo: sem acento, sem caractere inválido. */
export function sanitizarNomeArquivo(texto: string): string {
    return String(texto ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
}

/**
 * Monta o nome do arquivo KML juntando as partes informadas (propriedade, talhão,
 * análise, profundidade...), já sanitizadas. Ignora partes vazias e cai no
 * fallback quando nada sobra.
 */
export function montarNomeArquivoKml(partes: (string | number | null | undefined)[], fallback: string): string {
    const limpas = partes
        .map((parte) => sanitizarNomeArquivo(String(parte ?? '')))
        .filter((parte) => parte !== '')

    return limpas.length > 0 ? limpas.join('_') : fallback
}

/** Centroide de uma geometria (média recursiva das coordenadas). */
function getCentroid(coords: any): { lat: number; lng: number } {
    let totalLat = 0
    let totalLng = 0
    let count = 0

    const processar = (arr: any) => {
        if (typeof arr[0] === 'number') {
            totalLng += arr[0]
            totalLat += arr[1]
            count++
        } else {
            arr.forEach(processar)
        }
    }
    processar(coords)

    return {
        lat: count > 0 ? totalLat / count : 0,
        lng: count > 0 ? totalLng / count : 0,
    }
}

export interface MalhaImportada {
    /** Pontos normalizados (INDICE/NOME/LAT/LONG) usados na UI. */
    malha: GeoPointFeature[]
    /** GeoJSON (FeatureCollection) enviado ao backend em malha_de_pontos. */
    malhaShp: {
        features: any[]
        fileName: string
        type: string
    }
    /** Quantidade total de pontos. */
    total: number
}

/**
 * Extrai o número do ponto a partir das propriedades da feature.
 *
 * Procura, em ordem: `NOME` (em qualquer caixa), `AUTO_ID`, e por fim qualquer
 * chave que contenha "nome" ou "id". De cada candidata extrai o primeiro grupo de
 * dígitos, o que permite nomes alfanuméricos ('CC001' -> 1).
 *
 * Retorna `NaN` quando nenhuma candidata tem número — o chamador deve validar.
 */
export function extrairNomePonto(properties: any): number {
    const chaves = Object.keys(properties ?? {})

    const candidatas = [
        chaves.find((chave) => chave.toUpperCase() === 'NOME'),
        chaves.find((chave) => chave.toUpperCase() === 'AUTO_ID'),
        ...chaves.filter((chave) => /nome|id/i.test(chave)),
    ].filter(Boolean) as string[]

    let nome = NaN

    candidatas.some((chave) => {
        const digitos = String(properties[chave]).match(/\d+/)?.[0] ?? ''
        const valor = parseInt(digitos, 10)
        if (Number.isNaN(valor)) return false
        nome = valor
        return true
    })

    return nome
}

/**
 * Lê o arquivo e devolve TODAS as coleções (FeatureCollection) que ele contém.
 *
 * O caminho normal é o `shp()` direto. O fallback existe porque alguns clientes
 * enviam um zip contendo outros zips — um shapefile por talhão; sem ele esses
 * arquivos, aceitos em produção, seriam rejeitados como inválidos.
 */
export async function parseShapeZip(buffer: ArrayBuffer): Promise<any[]> {
    try {
        const resultado: any = await shp(buffer)
        return Array.isArray(resultado) ? resultado : [resultado]
    } catch (error) {
        const zip = await JSZip.loadAsync(buffer)
        const zipsInternos = Object.values(zip.files).filter(
            (item: any) => !item.dir && item.name.toLowerCase().endsWith('.zip')
        )

        // Não é zip-de-zips: o erro original descreve melhor o problema.
        if (zipsInternos.length === 0) throw error

        const parseados = await Promise.all(
            zipsInternos.map(async (item: any) => {
                const resultado: any = await shp(await item.async('arraybuffer'))
                const colecoes = Array.isArray(resultado) ? resultado : [resultado]
                // O nome do zip interno identifica o talhão na hora de escolher.
                colecoes.forEach((colecao: any) => {
                    colecao.fileName = item.name.replace(/\.zip$/i, '')
                })
                return colecoes
            })
        )

        return parseados.reduce((acc: any[], item: any[]) => acc.concat(item), [])
    }
}

/**
 * Converte UMA coleção (FeatureCollection) na malha usada pela UI e pelo payload.
 * Lança 'Arquivo Invalido' se não houver pontos ou se algum ponto não tiver
 * número identificável — sem isso a malha entraria com `NaN` e só quebraria
 * depois, no mapa.
 */
export function aplicarMalhaTalhao(colecao: any): MalhaImportada {
    const features = colecao?.features ?? []

    if (features.length === 0) {
        throw new Error('Arquivo Invalido')
    }

    const featuresTratadas = features.map((data: any) => {
        const nome = extrairNomePonto(data?.properties)

        if (Number.isNaN(nome)) {
            throw new Error('Arquivo Invalido')
        }

        data.color = '#ffffff'
        data.properties.NOME = nome
        return data
    })

    const malha: GeoPointFeature[] = featuresTratadas.map((data: any) => {
        let lat: number
        let long: number

        if (data.geometry.type === 'Point') {
            ;[long, lat] = data.geometry.coordinates
        } else {
            const centro = getCentroid(data.geometry.coordinates)
            lat = centro.lat
            long = centro.lng
        }

        return {
            INDICE: data.properties.INDICE ?? data.properties.AUTO_ID,
            NOME: data.properties.NOME,
            LATITUDE: lat,
            LONGITUDE: long,
        }
    })

    return {
        malha,
        malhaShp: { features: featuresTratadas, fileName: 'POINT', type: 'FeatureCollection' },
        total: malha.length,
    }
}

/**
 * Faz o parse de um shapefile (.shp/.zip) em malha de pontos.
 * Função pura: não toca em estado — retorna os dados ou lança em arquivo inválido.
 *
 * Quando o arquivo traz VÁRIAS coleções (zip multi-talhão), devolve-as em
 * `colecoes` para o chamador pedir ao usuário qual importar — a OS aceita um
 * único talhão.
 */
export async function parseShapefile(
    files: File[]
): Promise<{ malha: MalhaImportada | null; colecoes: any[] }> {
    if (!files || files.length === 0) return { malha: null, colecoes: [] }

    const buffer = await files[0].arrayBuffer()
    const colecoes = await parseShapeZip(buffer)

    if (colecoes.length > 1) {
        return { malha: null, colecoes }
    }

    return { malha: aplicarMalhaTalhao(colecoes[0]), colecoes: [] }
}

/**
 * Converte a malha_de_pontos salva (GeoJSON) em pontos normalizados para a UI.
 * Usado ao EDITAR/COPIAR uma OS existente (mesma lógica do PreencherDados antigo).
 */
export function malhaSalvaParaPontos(malhaDePontos: any): GeoPointFeature[] {
    const features = malhaDePontos?.features ?? []
    return features.map((data: any) => {
        let lat: number
        let long: number
        if (data.geometry?.type === 'Point') {
            ;[long, lat] = data.geometry.coordinates
        } else {
            const centro = getCentroid(data.geometry.coordinates)
            lat = centro.lat
            long = centro.lng
        }
        return {
            INDICE: data.properties?.INDICE || data.properties?.AUTO_ID,
            NOME: parseInt(data.properties?.NOME, 10) || parseInt(data.properties?.AUTO_ID, 10),
            LATITUDE: lat,
            LONGITUDE: long,
        }
    })
}

/** Adiciona a cor da análise a cada ponto. */
function adicionarCorPontos(pontos: any[], cor: string) {
    return (pontos ?? []).map((p) => ({ ...p, COR: cor }))
}

/**
 * Converte as analises salvas de uma OS em cards (AnaliseAdicionada) para a lista.
 * Mesma transformação do PreencherDados antigo.
 */
export function analisesSalvasParaCards(analises: any[]): import('../../../types').AnaliseAdicionada[] {
    return (analises ?? []).map((item) => ({
        id: item?.id || String(item?.cod_analises?.[0]?.cod ?? ''),
        cod_analise: Number(item?.cod_analises?.[0]?.cod),
        pontos_selecionados: adicionarCorPontos(item?.pontos, item?.cod_analises?.[0]?.cor),
        profundidade: item?.profundidade,
        analise_nome: item?.cod_analises?.[0]?.nome,
        valor: item?.cod_analises?.[0]?.valor,
        cor: item?.cod_analises?.[0]?.cor,
        valor_analise: (
            Number(item?.cod_analises?.[0]?.valor) * (item?.pontos?.length ?? 0)
        ).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }),
    }))
}
